/** Shared pieces for the Background Jobs tabs. */
import type { ExecutionConfig, ExecutionType, ScheduleType } from '../../../types/background-job.types';

/** Seven keys. The guide says six and that schedules have none of their own, but the
 *  toggle endpoint requires BACKGROUND_JOB_SCHEDULE.TOGGLE — confirmed live. */
export const BACKGROUND_JOB_PERMISSIONS = {
  read: 'BACKGROUND_JOB.READ',
  create: 'BACKGROUND_JOB.CREATE',
  update: 'BACKGROUND_JOB.UPDATE',
  delete: 'BACKGROUND_JOB.DELETE',
  execute: 'BACKGROUND_JOB.EXECUTE',
  runRead: 'BACKGROUND_JOB_RUN.READ',
  // seventh key, undocumented in the guide but enforced by the toggle endpoint
  toggle: 'BACKGROUND_JOB_SCHEDULE.TOGGLE',
} as const;

export const EXECUTION_TYPE_OPTIONS = [
  { label: 'Function', value: 'FUNCTION' },
  { label: 'API Call', value: 'API_CALL' },
  { label: 'LLM Prompt', value: 'LLM_PROMPT' },
];

/**
 * Last-resort options for the function dropdown.
 *
 * The real list comes from `backgroundJobService.listFunctionKeys()` — the
 * registry `FunctionExecutor` validates against. These three are kept only so
 * the form still renders something if that call fails; they were the whole list
 * once, and by then the backend registered six, so three registered functions
 * could not be selected at all.
 */
export const FUNCTION_KEY_FALLBACK = [
  { label: 'heartbeat', value: 'heartbeat' },
  { label: 'purge_expired_refresh_tokens', value: 'purge_expired_refresh_tokens' },
  { label: 'reconcile_stale_job_runs', value: 'reconcile_stale_job_runs' },
];

export const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']
  .map((m) => ({ label: m, value: m }));

export const SCHEDULE_TYPE_OPTIONS = [
  { label: 'Cron', value: 'CRON' },
  { label: 'Interval', value: 'INTERVAL' },
  { label: 'Once', value: 'ONCE' },
];

export const RUN_STATUS_OPTIONS = ['RUNNING', 'SUCCESS', 'FAILED', 'RETRYING', 'CANCELLED']
  .map((s) => ({ label: s[0] + s.slice(1).toLowerCase(), value: s }));

export const TRIGGERED_BY_OPTIONS = [
  { label: 'Scheduler', value: 'SYSTEM_SCHEDULE' },
  { label: 'Manual', value: 'MANUAL_USER' },
];

/**
 * The engine's finest resolution. The backend rejects a shorter interval outright
 * (JOB_POLL_INTERVAL_SECONDS, default 5) but does not expose the setting, so this
 * mirrors the default — see the note about surfacing it from /dashboard.
 */
export const POLL_INTERVAL_SECONDS = 5;

const JOB_KEY_RE = /^[A-Z][A-Z0-9_]*$/;

export function jobKeyError(key: string): string {
  if (!key.trim()) return 'A job key is required.';
  if (!JOB_KEY_RE.test(key)) return 'Upper case letters, digits and underscores only.';
  return '';
}

/** A fresh config for a type — never merged with the previous one (`extra="forbid"`). */
export function blankConfig(t: ExecutionType): ExecutionConfig {
  if (t === 'FUNCTION') return { functionKey: '', timeoutSeconds: 60 };
  if (t === 'API_CALL') return { method: 'GET', url: '', timeoutSeconds: 30, expectedStatus: [200] };
  return { promptTemplate: '', maxTokens: 1024, outputKey: 'output' };
}

/** One-line summary of what a job actually does, for the list column. */
export function describeConfig(t: ExecutionType, c: ExecutionConfig | undefined): string {
  const cfg = (c || {}) as Record<string, unknown>;
  if (t === 'FUNCTION') return String(cfg.functionKey || '—');
  if (t === 'API_CALL') return `${cfg.method || 'GET'} ${cfg.url || cfg.path || '—'}`;
  const p = String(cfg.promptTemplate || '');
  return p.length > 60 ? `${p.slice(0, 60)}…` : (p || '—');
}

/** Blank timing for a schedule type — the other two fields must be absent. */
export function blankTiming(t: ScheduleType) {
  return {
    cronExpression: t === 'CRON' ? '0 2 * * *' : undefined,
    intervalSeconds: t === 'INTERVAL' ? 300 : undefined,
    runAtDateTime: undefined as string | undefined,
  };
}

/** Human summary of a schedule's timing. */
export function describeTiming(s: {
  scheduleType: string; cronExpression?: string | null;
  intervalSeconds?: number | null; runAtDateTime?: string | null;
}): string {
  if (s.scheduleType === 'CRON') return s.cronExpression || '—';
  if (s.scheduleType === 'INTERVAL') {
    const n = s.intervalSeconds || 0;
    if (n % 3600 === 0) return `every ${n / 3600}h`;
    if (n % 60 === 0) return `every ${n / 60}m`;
    return `every ${n}s`;
  }
  return s.runAtDateTime ? String(s.runAtDateTime).replace('T', ' ').slice(0, 16) : '—';
}

/**
 * Datetimes are naive IST server-side. Sending a UTC string for a locally-picked
 * time shifts it by 5½ hours, so the value is serialised without a zone.
 */
export function toNaiveIso(d: Date | null): string | undefined {
  if (!d) return undefined;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:00`;
}

export function fromNaiveIso(s?: string | null): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}
